import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { apiErrorToast } from '@/lib/apiErrorToast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import {
  IndianRupee, TrendingUp, TrendingDown, Receipt, Plus, BarChart3,
  ArrowUp, ArrowDown, Lightbulb, Sparkles, PieChart,
  ShieldCheck, ShieldAlert, Wrench, RefreshCw, AlertTriangle, CheckCircle2,
  Radio, FileText, Banknote, Undo2
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart as RechartPie, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import YearEndReportCard from '@/components/finance/YearEndReportCard';
// Phase 266j — Cash drawer reconciliation (offline-refund tally for end-of-day till matching)
import CashDrawerReconciliationTab from '@/components/finance/CashDrawerReconciliationTab';

const expenseCategories = ['Rent', 'Salary', 'Equipment', 'Maintenance', 'Utilities', 'Marketing', 'Insurance', 'Transport', 'Food', 'Other'];
const COLORS = ['#0f766e', '#14b8a6', '#334155', '#f59e0b', '#f43f5e', '#6366f1', '#8b5cf6', '#ec4899'];

export default function Finance() {
  const [pnl, setPnl] = useState(null);
  const [ytdRefunds, setYtdRefunds] = useState({ amount: 0, count: 0 }); // Phase 276b — YTD refund radar tile
  // Phase 276c — drill-down panel for the Refunds YTD tile.
  const [refundsPanelOpen, setRefundsPanelOpen] = useState(false);
  const [refundsList, setRefundsList] = useState(null); // null = not loaded yet
  const [refundsLoading, setRefundsLoading] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [expenseForm, setExpenseForm] = useState({ category: '', amount: '', description: '', date: new Date().toISOString().split('T')[0], vendor: '' });
  // Phase 65.5 — Self-Heal & Index Health
  const [indexStatus, setIndexStatus] = useState(null);   // { healthy, status, has_index, duplicate_groups, message }
  const [scanResult, setScanResult] = useState(null);     // dry-run report
  const [showDedupeDialog, setShowDedupeDialog] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [dedupeRunning, setDedupeRunning] = useState(false);
  // Phase 90 — WhatsApp/Twilio configuration warning banner. We don't BLOCK
  // billing — academies can still operate without WhatsApp — but we surface
  // a clear inline prompt so they know parent alerts won't dispatch silently.
  const [twilioConfigured, setTwilioConfigured] = useState(true); // default true → no banner flash on initial paint
  const [resendConfigured, setResendConfigured] = useState(true);

  const fetchIndexStatus = useCallback(async () => {
    try {
      const r = await api.get('/fees/billing-index-status');
      setIndexStatus(r.data);
    } catch (err) {
      // 403 if user role isn't admin — silently ignore (badge hidden)
      setIndexStatus(null);
    }
  }, []);

  const handleScanDuplicates = async () => {
    setScanning(true);
    try {
      const r = await api.post('/fees/dedupe', { dry_run: true });
      setScanResult(r.data);
      setShowDedupeDialog(true);
      if ((r.data?.would_remove_count || 0) === 0) {
        toast.success('No duplicate bills found — your billing engine is clean.');
        setShowDedupeDialog(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const handleConfirmDedupe = async () => {
    setDedupeRunning(true);
    try {
      const r = await api.post('/fees/dedupe', {});
      const removed = r.data?.total_fees_removed || 0;
      const groups = r.data?.duplicate_groups || 0;
      const indexFlag = r.data?.index_recreated ? ' Billing safeguard activated.' : '';
      toast.success(`Financial records sanitized. ${removed} duplicate${removed === 1 ? '' : 's'} removed across ${groups} student${groups === 1 ? '' : 's'}.${indexFlag}`);
      setShowDedupeDialog(false);
      setScanResult(null);
      fetchIndexStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Dedupe failed');
    } finally {
      setDedupeRunning(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      // Phase 276b — YTD refunds tile: refunded_amount/count scoped to current calendar year.
      const ytdStart = `${new Date().getFullYear()}-01-01`;
      const [pnlRes, expRes, chartRes, ytdRes] = await Promise.all([
        api.get('/finance/pnl'),
        api.get('/expenses'),
        api.get('/dashboard/revenue-chart'),
        api.get('/finance/pnl', { params: { start_date: ytdStart } })
      ]);
      setPnl(pnlRes.data);
      setExpenses(expRes.data);
      setChartData(chartRes.data);
      setYtdRefunds({
        amount: Number(ytdRes.data?.refunded_amount || 0),
        count: Number(ytdRes.data?.refunded_count || 0),
      });
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  const fetchInsights = useCallback(async () => {
    try {
      const res = await api.post('/ai/insights', { type: 'finance' });
      setInsights(res.data.insights || []);
    } catch (err) { console.error(err); }
  }, []);

  // Phase 276c — open the refund drill-down slide-over.  Lazy fetch on first
  // open, then cache in state so re-opening is instant.  Always scoped to
  // YTD so the panel matches the radar tile that triggered it.
  const openRefundsPanel = useCallback(async () => {
    setRefundsPanelOpen(true);
    if (refundsList !== null) return;
    setRefundsLoading(true);
    try {
      const ytdStart = `${new Date().getFullYear()}-01-01`;
      const res = await api.get('/finance/refunds', { params: { start_date: ytdStart } });
      setRefundsList(res.data?.refunds || []);
    } catch (err) {
      console.error(err);
      setRefundsList([]);
    } finally {
      setRefundsLoading(false);
    }
  }, [refundsList]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (activeTab === 'insights') fetchInsights(); }, [activeTab, fetchInsights]);
  useEffect(() => { fetchIndexStatus(); }, [fetchIndexStatus]);
  // Phase 90 — load communication-channel configuration once per page mount.
  useEffect(() => {
    api.get('/settings').then(r => {
      setTwilioConfigured(!!r.data?.twilio_configured);
      setResendConfigured(!!r.data?.resend_configured);
    }).catch(() => {/* 403 for non-admin — banner stays hidden */});
  }, []);

  const handleAddExpense = async (e) => {
    e.preventDefault();
    try {
      await api.post('/expenses', { ...expenseForm, amount: parseFloat(expenseForm.amount) });
      toast.success('Expense added');
      setShowAddExpense(false);
      setExpenseForm({ category: '', amount: '', description: '', date: new Date().toISOString().split('T')[0], vendor: '' });
      fetchData();
    } catch (err) { apiErrorToast(err, 'Failed to add expense'); }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try { await api.delete(`/expenses/${id}`); toast.success('Deleted'); fetchData(); }
    catch (err) { apiErrorToast(err, 'Failed to delete expense'); }
  };

  const pieData = pnl ? Object.entries(pnl.expenses_by_category || {}).map(([name, value]) => ({ name, value })) : [];

  if (loading) return (
    <div className="space-y-4" data-testid="finance-loading">
      <Skeleton className="h-8 w-48" />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
      <Skeleton className="h-64 rounded-xl" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="finance-page">
      {/* Phase 90 — Comms-channel configuration warning. Non-blocking, dismissable */}
      {(!twilioConfigured || !resendConfigured) && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30 p-3 flex items-start gap-3" data-testid="finance-comms-warning">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
              {(!twilioConfigured && !resendConfigured)
                ? 'WhatsApp & Email channels not configured'
                : !twilioConfigured ? 'WhatsApp channel not configured' : 'Email channel not configured'}
            </p>
            <p className="text-[11px] text-amber-800 dark:text-amber-300 mt-0.5 leading-snug">
              {!twilioConfigured && 'Automated WhatsApp fee reminders to parents will not dispatch until you add your Twilio &amp; DLT credentials. '}
              {!resendConfigured && 'Fee receipts and reminder emails won\'t go out until you connect Resend. '}
              <a href="/settings" className="underline font-medium hover:text-amber-700 dark:hover:text-amber-200">
                Configure in Settings →
              </a>
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-semibold">Finance</h1>
          <p className="text-sm text-muted-foreground">Track revenue, expenses, and profitability</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Phase 138H.b — Quick link to the Dunning Control Center */}
          <Button
            size="sm" variant="outline"
            asChild
            data-testid="finance-dunning-cta">
            <a href="/finance/dunning" className="flex items-center gap-1.5">
              <Radio className="h-4 w-4" /> Dunning Center
            </a>
          </Button>
          <Dialog open={showAddExpense} onOpenChange={setShowAddExpense}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="finance-add-expense-button"><Plus className="h-4 w-4 mr-1" /> Add Expense</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Expense</DialogTitle></DialogHeader>
            <form onSubmit={handleAddExpense} className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Category *</Label>
                <Select value={expenseForm.category} onValueChange={(v) => setExpenseForm({...expenseForm, category: v})}>
                  <SelectTrigger data-testid="expense-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>{expenseCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Amount (₹) *</Label><Input type="number" value={expenseForm.amount} onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})} placeholder="5000" required data-testid="expense-amount-input" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Date *</Label><Input type="date" value={expenseForm.date} onChange={(e) => setExpenseForm({...expenseForm, date: e.target.value})} required /></div>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Description</Label><Input value={expenseForm.description} onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})} placeholder="Monthly rent" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Vendor</Label><Input value={expenseForm.vendor} onChange={(e) => setExpenseForm({...expenseForm, vendor: e.target.value})} placeholder="Vendor name" /></div>
              <Button type="submit" className="w-full" data-testid="expense-add-submit">Add Expense</Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* KPI Cards */}
      {pnl && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          <Card className="hover:shadow-md hover:-translate-y-px transition-[box-shadow,transform] duration-200" data-testid="kpi-total-revenue">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-muted-foreground">Total Revenue</span>
                <div className="h-8 w-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <TrendingUp className="h-4 w-4 text-emerald-600" />
                </div>
              </div>
              <p className="text-2xl font-display font-semibold tabular-nums" data-testid="kpi-total-revenue-value">₹{pnl.total_revenue.toLocaleString()}</p>
              {pnl.refunded_amount > 0 ? (
                <p className="text-xs text-amber-600 font-medium mt-1" data-testid="kpi-total-revenue-refund-line">
                  Net of ₹{Number(pnl.refunded_amount).toLocaleString()} refunded
                  {pnl.refunded_count ? ` · ${pnl.refunded_count} ${pnl.refunded_count === 1 ? 'fee' : 'fees'}` : ''}
                </p>
              ) : (
                <p className="text-xs text-emerald-600 font-medium mt-1">From fee collections</p>
              )}
            </CardContent>
          </Card>
          <Card className="hover:shadow-md hover:-translate-y-px transition-[box-shadow,transform] duration-200" data-testid="kpi-total-expenses">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-muted-foreground">Total Expenses</span>
                <div className="h-8 w-8 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <TrendingDown className="h-4 w-4 text-red-600" />
                </div>
              </div>
              <p className="text-2xl font-display font-semibold tabular-nums">₹{pnl.total_expenses.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground mt-1">{Object.keys(pnl.expenses_by_category || {}).length} categories</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-md hover:-translate-y-px transition-[box-shadow,transform] duration-200" data-testid="kpi-net-profit">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-muted-foreground">Net Profit</span>
                <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${pnl.net_profit >= 0 ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                  {pnl.net_profit >= 0 ? <ArrowUp className="h-4 w-4 text-emerald-600" /> : <ArrowDown className="h-4 w-4 text-red-600" />}
                </div>
              </div>
              <p className={`text-2xl font-display font-semibold tabular-nums ${pnl.net_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>₹{pnl.net_profit.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground mt-1">{pnl.profit_margin}% margin</p>
            </CardContent>
          </Card>
          {/* Phase 276b/c — Refunds radar tile (YTD).  Clickable → drill-down panel. */}
          <Card
            className={`hover:shadow-md hover:-translate-y-px transition-[box-shadow,transform] duration-200 ${ytdRefunds.count > 0 ? 'cursor-pointer hover:ring-1 hover:ring-amber-300' : 'cursor-default'}`}
            data-testid="kpi-refunds-ytd"
            role={ytdRefunds.count > 0 ? 'button' : undefined}
            tabIndex={ytdRefunds.count > 0 ? 0 : -1}
            onClick={ytdRefunds.count > 0 ? openRefundsPanel : undefined}
            onKeyDown={(e) => {
              if (ytdRefunds.count > 0 && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                openRefundsPanel();
              }
            }}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-muted-foreground">Refunds YTD</span>
                <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${ytdRefunds.amount > 0 ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-slate-100 dark:bg-slate-800'}`}>
                  <Undo2 className={`h-4 w-4 ${ytdRefunds.amount > 0 ? 'text-amber-600' : 'text-slate-400'}`} />
                </div>
              </div>
              <p className={`text-2xl font-display font-semibold tabular-nums ${ytdRefunds.amount > 0 ? 'text-amber-600' : 'text-muted-foreground'}`} data-testid="kpi-refunds-ytd-value">
                ₹{ytdRefunds.amount.toLocaleString()}
              </p>
              <p className="text-xs text-muted-foreground mt-1" data-testid="kpi-refunds-ytd-count">
                {ytdRefunds.count === 0
                  ? 'No refunds this year'
                  : `${ytdRefunds.count} ${ytdRefunds.count === 1 ? 'fee refunded' : 'fees refunded'} · view details`}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full sm:w-auto overflow-x-auto justify-start no-scrollbar">
          <TabsTrigger value="overview" data-testid="tab-finance-overview" className="shrink-0"><BarChart3 className="h-4 w-4 mr-1" /> Overview</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses" className="shrink-0"><Receipt className="h-4 w-4 mr-1" /> Expenses</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-finance-reports" className="shrink-0"><FileText className="h-4 w-4 mr-1" /> Year-End Reports</TabsTrigger>
          <TabsTrigger value="cash-drawer" data-testid="tab-finance-cash-drawer" className="shrink-0"><Banknote className="h-4 w-4 mr-1" /> Cash Drawer</TabsTrigger>
          <TabsTrigger value="insights" data-testid="tab-insights" className="shrink-0"><Sparkles className="h-4 w-4 mr-1" /> AI Insights</TabsTrigger>
          <TabsTrigger value="maintenance" data-testid="tab-finance-maintenance" className="shrink-0"><Wrench className="h-4 w-4 mr-1" /> Data Maintenance</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid lg:grid-cols-3 gap-4 md:gap-6">
            <Card className="lg:col-span-2" data-testid="finance-chart-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Revenue vs Expenses Trend</CardTitle>
              </CardHeader>
              <CardContent>
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 22%, 88%)" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => '₹' + (v/1000) + 'K'} />
                      <Tooltip formatter={(value) => ['₹' + value.toLocaleString()]} />
                      <Legend />
                      <Bar dataKey="revenue" name="Revenue" fill="#0f766e" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="expenses" name="Expenses" fill="#f43f5e" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">No data available yet.</div>
                )}
              </CardContent>
            </Card>
            <Card data-testid="expense-breakdown-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Expense Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                {pieData.length > 0 ? (
                  <div>
                    <ResponsiveContainer width="100%" height={200}>
                      <RechartPie>
                        <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value">
                          {pieData.map((_, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(value) => ['₹' + value.toLocaleString()]} />
                      </RechartPie>
                    </ResponsiveContainer>
                    <div className="space-y-2 mt-3">
                      {pieData.map((item, i) => (
                        <div key={item.name} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <div className="h-3 w-3 rounded-sm" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                            <span className="text-muted-foreground">{item.name}</span>
                          </div>
                          <span className="font-medium tabular-nums">₹{item.value.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="h-48 flex items-center justify-center text-sm text-muted-foreground">No expenses recorded yet.</div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-28">ID</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead className="whitespace-nowrap">Description</TableHead>
                      <TableHead className="whitespace-nowrap">Date</TableHead>
                      <TableHead className="whitespace-nowrap">Vendor</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expenses.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">No expenses yet. Click "Add Expense" to track spending.</TableCell></TableRow>
                    ) : expenses.map((exp) => (
                      <TableRow key={exp.id} data-testid={`expense-row-${exp.id}`}>
                        <TableCell><span className="text-xs font-mono text-muted-foreground">{exp.custom_id || '-'}</span></TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{exp.category}</Badge></TableCell>
                        <TableCell className="font-semibold tabular-nums">₹{exp.amount.toLocaleString()}</TableCell>
                        <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{exp.description || '-'}</TableCell>
                        <TableCell className="whitespace-nowrap text-sm">{exp.date}</TableCell>
                        <TableCell className="whitespace-nowrap text-sm">{exp.vendor || '-'}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteExpense(exp.id)}>
                            <Receipt className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Insights Tab */}
        <TabsContent value="insights">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {insights.length === 0 ? (
              <Card className="md:col-span-2 lg:col-span-3 border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <Sparkles className="h-12 w-12 text-muted-foreground/50 mb-4" />
                  <h3 className="text-lg font-semibold mb-1">AI Insights</h3>
                  <p className="text-sm text-muted-foreground">Add more data to get personalized AI-powered financial insights</p>
                  <Badge variant="secondary" className="mt-3 text-xs">Powered by Gemini AI</Badge>
                </CardContent>
              </Card>
            ) : insights.map((insight, i) => (
              <Card key={i} className="hover:shadow-md hover:-translate-y-px transition-[box-shadow,transform] duration-200" data-testid={`insight-card-${i}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Lightbulb className="h-4 w-4 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-sm mb-1">{insight.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{insight.insight}</p>
                      {insight.action && (
                        <Button size="sm" variant="outline" className="mt-3 h-7 text-xs">{insight.action}</Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Phase 249 — Year-End Fee Reports Tab */}
        <TabsContent value="reports">
          <div className="grid lg:grid-cols-2 gap-4 md:gap-6">
            <YearEndReportCard mode="owner" />
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">What's inside a Year-End Report?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>A single, signed-letterhead PDF that includes:</p>
                <ul className="list-disc pl-5 space-y-1 text-xs">
                  <li><strong className="text-foreground">Section A</strong> — every tuition invoice issued during the Indian fiscal year, with status (paid / pending / overdue).</li>
                  <li><strong className="text-foreground">Section B</strong> — every payment received, dated and tagged with method.</li>
                  <li><strong className="text-foreground">Scholarship adjustment</strong> — surfaced explicitly with notes if applied.</li>
                  <li><strong className="text-foreground">Totals</strong> — invoiced, paid, and outstanding balance for the year.</li>
                </ul>
                <p className="pt-2 text-xs">Bulk download produces a ZIP of one PDF per student with any financial activity in the selected FY. Useful for tax-season hand-offs to all parents in a single batch.</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Phase 266j — Cash Drawer Reconciliation Tab */}
        <TabsContent value="cash-drawer">
          <CashDrawerReconciliationTab />
        </TabsContent>

        {/* Data Maintenance Tab — Phase 65.5 Self-Heal */}
        <TabsContent value="maintenance">
          <div className="grid lg:grid-cols-3 gap-4 md:gap-6">
            {/* Dedupe Self-Heal Card */}
            <Card className="lg:col-span-2 border-l-4 border-l-amber-500" data-testid="dedupe-card">
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-base font-display flex items-center gap-2">
                      <Wrench className="h-4 w-4 text-amber-600" /> Scan & Fix Duplicate Bills
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">
                      One-click cleanup for accidental double-billing. Keeps the oldest pending invoice per (student · billing-type · period) and removes the rest. Paid invoices are never touched.
                    </p>
                  </div>
                  <Button
                    onClick={handleScanDuplicates}
                    disabled={scanning || dedupeRunning}
                    className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                    data-testid="dedupe-scan-button"
                  >
                    {scanning ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> : <Wrench className="h-4 w-4 mr-1.5" />}
                    {scanning ? 'Scanning…' : 'Scan & Fix Duplicate Bills'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50/40 dark:bg-amber-950/10 dark:border-amber-800 p-4">
                  <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1.5 uppercase tracking-wider">How it works</p>
                  <ol className="space-y-1.5 text-sm text-muted-foreground list-decimal pl-4">
                    <li><span className="font-medium text-foreground">Scan</span> — finds groups of identical pending fees (same student, type, period).</li>
                    <li><span className="font-medium text-foreground">Review</span> — confirm in the dialog before anything is deleted.</li>
                    <li><span className="font-medium text-foreground">Self-Heal</span> — duplicates are removed and the unique-billing safeguard is auto-activated to block future double-bills.</li>
                  </ol>
                </div>
              </CardContent>
            </Card>

            {/* Status Card */}
            <Card data-testid="billing-status-card">
              <CardHeader>
                <CardTitle className="text-base font-display flex items-center gap-2">
                  {indexStatus?.healthy
                    ? <ShieldCheck className="h-4 w-4 text-emerald-600" />
                    : <ShieldAlert className="h-4 w-4 text-amber-600" />}
                  Billing Engine Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {indexStatus ? (
                  <>
                    <div className={`rounded-lg p-3 border ${
                      indexStatus.healthy
                        ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-900'
                        : 'bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-900'
                    }`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`h-2.5 w-2.5 rounded-full ${indexStatus.healthy ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500 animate-pulse'}`} />
                        <span className={`text-sm font-semibold ${indexStatus.healthy ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'}`}>
                          {indexStatus.healthy ? 'Billing Engine Secured' : 'Maintenance Required'}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{indexStatus.message}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="rounded-md bg-muted/40 px-3 py-2">
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Unique Index</p>
                        <p className="font-semibold tabular-nums">{indexStatus.has_index ? <span className="text-emerald-600 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Active</span> : <span className="text-amber-600 inline-flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />Pending</span>}</p>
                      </div>
                      <div className="rounded-md bg-muted/40 px-3 py-2">
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Duplicate Groups</p>
                        <p className="font-semibold tabular-nums">{indexStatus.duplicate_groups}</p>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" className="w-full h-8 text-xs" onClick={fetchIndexStatus} data-testid="billing-status-refresh">
                      <RefreshCw className="h-3 w-3 mr-1" /> Re-check status
                    </Button>
                  </>
                ) : (
                  <Skeleton className="h-24 w-full rounded-md" />
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Phase 65.5 — Footer System Status Badge (visible from any tab) */}
      {indexStatus && (
        <div className="flex items-center justify-end pt-2 border-t border-border/50">
          <button
            onClick={() => setActiveTab('maintenance')}
            className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium transition-colors hover:opacity-80 ${
              indexStatus.healthy
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900'
                : 'bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-900'
            }`}
            title={indexStatus.message}
            data-testid="billing-status-badge"
          >
            <span className={`h-1.5 w-1.5 rounded-full ${indexStatus.healthy ? 'bg-emerald-500' : 'bg-amber-500'} animate-pulse`} />
            {indexStatus.healthy ? 'Billing Engine Secured' : 'Maintenance Required'}
          </button>
        </div>
      )}

      {/* Dedupe Confirmation Dialog */}
      <Dialog
        open={showDedupeDialog}
        onOpenChange={(open) => { if (!dedupeRunning) setShowDedupeDialog(open); }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="dedupe-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
              <AlertTriangle className="h-5 w-5" /> Duplicate Bills Found
            </DialogTitle>
          </DialogHeader>
          {scanResult && (
            <div className="space-y-3 mt-2">
              <p className="text-sm">
                Found <strong className="text-amber-700 dark:text-amber-400">{scanResult.duplicate_groups}</strong> duplicate fee group{scanResult.duplicate_groups === 1 ? '' : 's'} ·{' '}
                <strong className="text-amber-700 dark:text-amber-400">{scanResult.would_remove_count}</strong> extra invoice{scanResult.would_remove_count === 1 ? '' : 's'} will be removed.
              </p>
              <p className="text-xs text-muted-foreground">
                We will keep the <strong className="text-foreground">oldest</strong> pending invoice in each group and remove the rest. Paid records are never touched.
              </p>
              {scanResult.details && scanResult.details.length > 0 && (
                <div className="rounded-md border bg-muted/30 max-h-56 overflow-y-auto" data-testid="dedupe-preview-list">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Period</TableHead>
                        <TableHead className="text-xs">Type</TableHead>
                        <TableHead className="text-xs text-right">Removing</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {scanResult.details.map((d, i) => (
                        <TableRow key={`${d.student_id}-${d.period}-${i}`}>
                          <TableCell className="text-xs font-mono">{d.period}</TableCell>
                          <TableCell className="text-xs"><Badge variant="outline" className="text-[10px]">{d.fee_type}</Badge></TableCell>
                          <TableCell className="text-xs text-right tabular-nums text-red-600">−{d.removed_ids?.length || 0}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setShowDedupeDialog(false)} disabled={dedupeRunning} data-testid="dedupe-cancel">
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={handleConfirmDedupe}
                  disabled={dedupeRunning}
                  data-testid="dedupe-confirm"
                >
                  {dedupeRunning ? <RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                  {dedupeRunning ? 'Sanitizing…' : 'Yes, remove duplicates'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Phase 276c — Refunds drill-down slide-over.  Lists every refunded fee
          in the current calendar year so academy owners can spot service
          quality issues at a glance and drill into the underlying reason. */}
      <Sheet open={refundsPanelOpen} onOpenChange={setRefundsPanelOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="refunds-drilldown-panel">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Undo2 className="h-5 w-5 text-amber-600" />
              Refunds YTD
            </SheetTitle>
            <SheetDescription>
              Every fee refunded in {new Date().getFullYear()}. Sorted by refund date (newest first).
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-3" data-testid="refunds-drilldown-list">
            {refundsLoading && (
              <>
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
              </>
            )}

            {!refundsLoading && refundsList && refundsList.length === 0 && (
              <div className="text-center py-12 text-muted-foreground text-sm" data-testid="refunds-empty">
                No refunds recorded this year.
              </div>
            )}

            {!refundsLoading && refundsList && refundsList.map((r) => {
              const date = r.refund_initiated_at
                ? new Date(r.refund_initiated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                : '—';
              const method = r.refund_method === 'offline'
                ? `Offline · ${(r.refund_offline_method || 'cash').replace(/_/g, ' ')}`
                : 'Razorpay';
              const isProcessing = r.refund_status === 'processing';
              return (
                <div
                  key={r.id}
                  className="rounded-lg border border-border bg-card p-3 hover:border-amber-300 transition-colors"
                  data-testid={`refund-row-${r.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate" data-testid="refund-student-name">
                        {r.student_name || 'Unknown student'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {r.fee_type || 'fee'} · {date}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-semibold tabular-nums text-amber-600">
                        ₹{Number(r.refund_amount || 0).toLocaleString()}
                      </p>
                      <Badge
                        variant="outline"
                        className={`mt-1 text-[10px] py-0 px-1.5 ${isProcessing ? 'border-amber-300 text-amber-700' : 'border-emerald-300 text-emerald-700'}`}
                      >
                        {isProcessing ? 'Processing' : 'Refunded'}
                      </Badge>
                    </div>
                  </div>
                  {r.refund_reason && (
                    <p className="text-xs text-foreground/80 mt-2 italic" data-testid="refund-reason">
                      “{r.refund_reason}”
                    </p>
                  )}
                  <p className="text-[10px] text-muted-foreground mt-1.5">
                    via {method}{r.refund_initiated_by ? ` · ${r.refund_initiated_by}` : ''}
                  </p>
                </div>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
